/**
 * Supabase client — singleton, lazy-initialised from
 * `VITE_SUPABASE_URL` + `VITE_SUPABASE_ANON_KEY`.
 *
 * Auth model:
 *   - Email + password (with email-verification flow).
 *   - Google OAuth (provider configured in the Supabase dashboard).
 *   - Forgot-password reset email.
 *   - Session lives in localStorage by default (Supabase manages it
 *     via `supabase.auth.getSession()` / `onAuthStateChange`).
 *
 * The auth Bearer token used by the API client (`@/api/http`) is the
 * Supabase access JWT — pulled fresh on every request so a near-
 * expiration token gets rotated transparently.
 *
 * If env vars are missing the helpers throw a clear error rather
 * than degrade to mock auth — the mock is gone.
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

// ─── Env ───────────────────────────────────────────────────────────

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim() ?? '';
const SUPABASE_ANON_KEY =
  (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim() ?? '';

/** True when both Supabase env vars are present and non-empty. The
 *  auth pages use this to render a clear "auth not configured"
 *  banner instead of crashing on first signup attempt. */
export function isSupabaseConfigured(): boolean {
  return Boolean(SUPABASE_URL) && Boolean(SUPABASE_ANON_KEY);
}

// ─── Client ────────────────────────────────────────────────────────

let _client: SupabaseClient | null = null;

/** Singleton accessor. Throws if env vars are missing — the auth
 *  store guards every call with `isSupabaseConfigured()` and the
 *  auth pages refuse to render their forms in that state. */
export function getSupabase(): SupabaseClient {
  if (_client) return _client;
  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase is not configured. Set VITE_SUPABASE_URL and ' +
        'VITE_SUPABASE_ANON_KEY before signing in.',
    );
  }
  _client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      // Keep tokens in localStorage and refresh them automatically.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true, // for OAuth redirect callback
      storage: window.localStorage,
    },
  });
  return _client;
}

// ─── Helpers used by `@/api/http` and the auth store ─────────────

/** Returns the active access JWT, or `null` if no session. Pulls
 *  through `getSession()` so a near-expiry token auto-refreshes. */
export async function getAccessToken(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const { data } = await getSupabase().auth.getSession();
  return data.session?.access_token ?? null;
}

/** Subscribe to session changes — auth store uses this to mirror
 *  Supabase state into its own UI-shaped slice. Returns an unsubscribe. */
export function subscribeAuthChange(
  cb: (session: Session | null) => void,
): () => void {
  if (!isSupabaseConfigured()) return () => {};
  const { data } = getSupabase().auth.onAuthStateChange((_event, session) => {
    cb(session);
  });
  return () => data.subscription.unsubscribe();
}

// ─── Admin detection ───────────────────────────────────────────────

/** Comma-separated list of admin emails, normalised to lowercase.
 *  Defaults to the founder addresses from the project brief; can be
 *  overridden at build time via `VITE_ADMIN_EMAILS` for staging
 *  deployments. */
const ADMIN_EMAILS: ReadonlySet<string> = new Set(
  (
    (import.meta.env.VITE_ADMIN_EMAILS as string | undefined) ??
    'yonathan@growing-intelligence.com,shalev.yoni@gmail.com'
  )
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean),
);

/** True when the email matches the static admin allow-list. */
export function isAdminEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return ADMIN_EMAILS.has(email.trim().toLowerCase());
}
